import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { SearchEntity, SearchPlace } from '@tsr/shared-types';
import { searchAll } from './api';

/**
 * #139 / #141 — Unified search surface. Lives as the leading group of the #130
 * command bar (folded in from its interim top-right placement); its result list
 * is an anchored popover that drops below the bar.
 *
 * Type a query → debounced GET /api/search → two grouped result lists (PLACES
 * from the NRCan geocoder, ENTITIES from the operator domain). Enter / click
 * dispatches the active row: places fly to their bbox (the Google-familiar
 * zoom); entities route through the existing deep-link handlers. Cmd/Ctrl-K
 * opens and focuses the box from anywhere. Fully keyboard-navigable (↑/↓/Enter/
 * Esc) and screen-reader-labelled (combobox + listbox + active-descendant).
 *
 * The component is presentation + interaction only — App owns dispatch + audit.
 */

const DEBOUNCE_MS = 200;
/** Don't fire on a single keystroke — too noisy. Two characters is enough to
 *  disambiguate and matches the server's substring search. */
const MIN_QUERY_LEN = 2;

export interface SearchBoxProps {
  onDispatchPlace: (place: SearchPlace) => void;
  onDispatchEntity: (entity: SearchEntity) => void;
}

type Row =
  | { type: 'place'; place: SearchPlace }
  | { type: 'entity'; entity: SearchEntity };

/** Short, glanceable label + glyph per entity kind. */
const ENTITY_META: Record<SearchEntity['kind'], { glyph: string; label: string }> = {
  camera: { glyph: '🎥', label: 'Camera' },
  response_asset: { glyph: '🚒', label: 'Resource' },
  active_fire: { glyph: '🔥', label: 'Fire' },
  community: { glyph: '🏘️', label: 'Community' },
  incident: { glyph: '⚠️', label: 'Incident' },
};

export function SearchBox({ onDispatchPlace, onDispatchEntity }: SearchBoxProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<SearchPlace[]>([]);
  const [entities, setEntities] = useState<SearchEntity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const listboxId = useId();

  // Flat, ordered row list (places first, then entities) — the model the keyboard
  // navigation and aria-activedescendant index into.
  const rows = useMemo<Row[]>(
    () => [
      ...places.map((place) => ({ type: 'place' as const, place })),
      ...entities.map((entity) => ({ type: 'entity' as const, entity })),
    ],
    [places, entities],
  );

  const optionId = useCallback((i: number) => `${listboxId}-opt-${i}`, [listboxId]);

  // Debounced fetch. Aborts the in-flight request on each new keystroke so a slow
  // response can never overwrite a newer one (last-write-wins by abort).
  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      abortRef.current?.abort();
      setPlaces([]);
      setEntities([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      void searchAll(q, ctrl.signal)
        .then((res) => {
          if (ctrl.signal.aborted) return;
          setPlaces(res.places);
          setEntities(res.entities);
          setActiveIndex(res.places.length + res.entities.length > 0 ? 0 : -1);
          setLoading(false);
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setPlaces([]);
          setEntities([]);
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  // Cmd/Ctrl-K opens + focuses from anywhere on the map.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the dropdown on an outside click (the input itself stays put).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const reset = useCallback(() => {
    setQuery('');
    setPlaces([]);
    setEntities([]);
    setActiveIndex(-1);
    setOpen(false);
  }, []);

  const dispatchRow = useCallback(
    (row: Row | undefined) => {
      if (!row) return;
      if (row.type === 'place') onDispatchPlace(row.place);
      else onDispatchEntity(row.entity);
      reset();
      inputRef.current?.blur();
    },
    [onDispatchPlace, onDispatchEntity, reset],
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (rows.length > 0) {
          setOpen(true);
          setActiveIndex((i) => (i + 1) % rows.length);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (rows.length > 0) {
          setOpen(true);
          setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        dispatchRow(rows[activeIndex >= 0 ? activeIndex : 0]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (query) reset();
        else inputRef.current?.blur();
      }
    },
    [rows, activeIndex, dispatchRow, query, reset],
  );

  const showDropdown = open && query.trim().length >= MIN_QUERY_LEN;
  const hasResults = rows.length > 0;

  return (
    <div ref={rootRef} className="relative w-full min-w-0">
      <div
        role="combobox"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
        aria-owns={listboxId}
        className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/80 px-2.5 py-1.5 backdrop-blur focus-within:border-slate-500"
      >
        <span aria-hidden className="text-slate-400 text-sm">🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          placeholder="Search places & map…  ⌘K"
          aria-label="Search places and map entities"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          autoComplete="off"
          spellCheck={false}
          data-testid="search-input"
          className="w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
        />
        {loading && <span aria-hidden className="text-[10px] text-slate-500">…</span>}
      </div>

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Search results"
          data-testid="search-results"
          className="absolute left-0 top-full z-50 mt-1 max-h-[60vh] w-72 max-w-[80vw] overflow-y-auto rounded-md border border-slate-700 bg-slate-900/95 py-1 text-sm shadow-xl backdrop-blur"
        >
          {!hasResults && (
            <li className="px-3 py-2 text-xs text-slate-500" aria-live="polite">
              {loading ? 'Searching…' : 'No matches'}
            </li>
          )}

          {places.length > 0 && (
            <li
              role="presentation"
              className="px-3 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
            >
              Places
            </li>
          )}
          {places.map((place, i) => {
            const idx = i;
            return (
              <ResultRow
                key={`p-${idx}`}
                id={optionId(idx)}
                active={idx === activeIndex}
                glyph="📍"
                kindLabel="Place"
                title={place.title}
                onSelect={() => dispatchRow(rows[idx])}
                onHover={() => setActiveIndex(idx)}
              />
            );
          })}

          {entities.length > 0 && (
            <li
              role="presentation"
              className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
            >
              On the map
            </li>
          )}
          {entities.map((entity, i) => {
            const idx = places.length + i;
            const meta = ENTITY_META[entity.kind];
            return (
              <ResultRow
                key={`e-${entity.kind}-${entity.id}`}
                id={optionId(idx)}
                active={idx === activeIndex}
                glyph={meta.glyph}
                kindLabel={meta.label}
                title={entity.name}
                detail={entity.detail}
                onSelect={() => dispatchRow(rows[idx])}
                onHover={() => setActiveIndex(idx)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface ResultRowProps {
  id: string;
  active: boolean;
  glyph: string;
  kindLabel: string;
  title: string;
  detail?: string | null;
  onSelect: () => void;
  onHover: () => void;
}

function ResultRow({
  id,
  active,
  glyph,
  kindLabel,
  title,
  detail,
  onSelect,
  onHover,
}: ResultRowProps): JSX.Element {
  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      // mousedown (not click) so the dispatch fires before the input's blur /
      // outside-click handler tears the dropdown down.
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
      className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 ${
        active ? 'bg-slate-700/70' : 'hover:bg-slate-800/60'
      }`}
    >
      <span aria-hidden className="w-4 shrink-0 text-center text-sm">{glyph}</span>
      <span className="min-w-0 flex-1 truncate text-slate-100">{title}</span>
      {detail && (
        <span className="shrink-0 truncate text-[11px] text-slate-400">{detail}</span>
      )}
      <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-600">
        {kindLabel}
      </span>
    </li>
  );
}
